import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'convex/react';

import { adminAPI, adminSession, type AdminPayment, type AdminProfileSlice, type AdminSubSlice, type UserAuditEntry, type UserAuditTrail, type UserDetail, type UserRow } from '@/lib/api';
import { api } from '../../../convex/functions/_generated/api';
import type { Id } from '../../../convex/functions/_generated/dataModel';
import { PaymentsTable } from '@/components/PaymentsTable';
import { friendlyError } from '@/lib/errors';
import { activeConfig } from '@/lib/environments';
import { UserActionsMenu } from '@/components/UserActionsMenu';
import { PLAN_LABELS, currentChoice } from '@/components/PlanPopover';

/**
 * Admin user detail page — /dashboard/users/:id.
 *
 * Single reactive query (api.admin.users.userDetail); renders user row
 * + their profile slice + aggregates + recent subscriptions. The Actions menu
 * in the header is the SAME UserActionsMenu used in the row of the
 * users list, so changing a user from anywhere in the dashboard
 * funnels through one component (and one set of confirmation modals).
 */
export function UserDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const token = adminSession.getToken();
  const data = useQuery(
    api.admin.users.userDetail,
    token && id ? { token, userId: id as Id<'users'> } : 'skip',
  );
  const loading = data === undefined;
  const detail = (data ?? null) as UserDetail | null;

  // Mutations from the Actions menu auto-refresh via useQuery's
  // reactive subscription. Keep the onChange callback as a thin
  // toast trigger so the admin still gets visual confirmation.
  const patchRow = useCallback((_u: UserRow) => {
    setToast('Saved.');
    setTimeout(() => setToast(null), 2200);
  }, []);

  if (loading || !detail) {
    return (
      <div>
        <BackLink />
        <div style={{ padding: 24, color: 'var(--ink-3)', fontSize: 13 }}>
          {err ? <ErrorBox>{err}</ErrorBox> : 'Loading…'}
        </div>
      </div>
    );
  }

  return (
    <div>
      <BackLink />

      {err && <ErrorBox>{err}</ErrorBox>}
      {toast && (
        <div style={{ background: 'rgba(56,142,60,0.10)', color: '#2e7d32', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16 }}>
          {toast}
        </div>
      )}

      {/* Header — name + status + actions menu */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>
            User
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 className="serif" style={{ fontSize: 30, fontWeight: 500, fontStyle: 'italic', margin: 0, overflowWrap: 'anywhere' }}>
              {detail.display_name || detail.email}
            </h2>
            <StatusBadge status={detail.status} />
          </div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, overflowWrap: 'anywhere' }}>
            {detail.email} · {detail.id}
          </div>
        </div>

        <UserActionsMenu
          user={detail}
          onChange={patchRow}
          onError={setErr}
          // Dev-only purge deletes the row — leave the (now-dead) detail page.
          onPurged={() => navigate('/dashboard/users')}
          // No View profile item — we're already on it.
          trigger={({ onClick, isOpen }) => (
            <button
              type="button"
              onClick={(e) => onClick(e.currentTarget)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: isOpen ? 'var(--bg)' : 'var(--surface)',
                color: 'var(--ink)',
                border: '1px solid var(--line-strong)',
                borderRadius: 100,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
              }}
            >
              Actions <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>▾</span>
            </button>
          )}
        />
      </div>

      {/* Stat tiles — at-a-glance aggregates the admin uses to triage. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        <Tile
          label="Plan"
          value={PLAN_LABELS[currentChoice(detail)]}
          sub={planSublabel(detail.subscriptions)}
        />
        <Tile label="Ratings received" value={String(detail.ratings_received)} />
        <Tile
          label="Open reports"
          value={String(detail.open_reports_count)}
          tone={detail.open_reports_count > 0 ? 'warn' : 'neutral'}
        />
        <Tile
          label="Auth"
          value={detail.auth_method}
        />
        <Tile
          label="Email verified"
          value={detail.email_verified ? 'Yes' : 'No'}
          tone={detail.email_verified ? 'neutral' : 'warn'}
        />
        <Tile
          label="Terms & Privacy"
          value={detail.terms_accepted ? 'Accepted' : 'Not accepted'}
          tone={detail.terms_accepted ? 'neutral' : 'warn'}
        />
        <Tile label="Joined" value={formatDate(detail.created_at)} />
        <Tile
          label="Last seen"
          value={detail.online ? 'Online' : detail.last_seen_at ? formatDate(detail.last_seen_at) : '—'}
        />
      </div>

      {/* Profile section */}
      <Section title="Profile">
        {detail.profile
          ? <ProfileCard profile={detail.profile} email={detail.email} />
          : <Hint>This user hasn&rsquo;t built a profile yet.</Hint>}
      </Section>

      {/* Subscriptions section */}
      <Section title="Subscriptions">
        {detail.subscriptions.length === 0
          ? <Hint>No subscriptions on file.</Hint>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {detail.subscriptions.map((sub) => (
                <SubCard key={sub.id} sub={sub} />
              ))}
            </div>
          )}
      </Section>

      {/* Payments — actual money in/out for this user. Subscriptions
          above are vendor-side state (status, period); this section
          is the charge ledger. */}
      <PaymentsSection userID={detail.id} onError={setErr} />

      {/* Audit trail — paginated feed of mutations, plus a "Download
          PDF" button the admin uses to generate chargeback evidence. */}
      <AuditTrailSection userID={detail.id} onError={setErr} />
    </div>
  );

  function BackLink() {
    return (
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--ink-3)',
          fontSize: 13,
          cursor: 'pointer',
          padding: '4px 0',
          marginBottom: 16,
          fontFamily: 'var(--font-ui)',
        }}
      >
        ← Back
      </button>
    );
  }
}

// ───── presentational helpers ─────────────────────────────────────────────

// planSublabel returns the dim second line for the Plan tile.
// Two cases produce a sublabel; everything else returns undefined
// (Tile renders without one).
//
//   - Active cancellation: cancel_at set AND current_period_end
//     in the future → "Cancels <date>"
//   - Recently expired (within 30 days): current_period_end in
//     the past → "Ended <date>" so the admin can confirm a
//     recently-lapsed sub even after the user has dropped to Free
//
// cancel_at + current_period_end are both written by the RC
// webhook.
function planSublabel(subs: AdminSubSlice[]): string | undefined {
  const now    = Date.now();
  const cutoff = now - 30 * 24 * 60 * 60 * 1000; // 30 days back

  // Active cancellation takes priority over a recently-expired
  // historical row — the live sub is the more relevant signal.
  const cancelling = subs.find((s) =>
    s.cancel_at
    && s.current_period_end
    && new Date(s.current_period_end).getTime() > now,
  );
  if (cancelling?.current_period_end) {
    return `Cancels ${formatDate(cancelling.current_period_end)}`;
  }

  // No active cancellation — look for a recently-expired sub so
  // the Plan tile (now showing Free) can explain the transition.
  const recentlyEnded = subs
    .filter((s) => s.current_period_end)
    .map((s) => ({ s, end: new Date(s.current_period_end!).getTime() }))
    .filter(({ end }) => end <= now && end > cutoff)
    .sort((a, b) => b.end - a.end)[0];
  if (recentlyEnded?.s.current_period_end) {
    return `Ended ${formatDate(recentlyEnded.s.current_period_end)}`;
  }
  return undefined;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </section>
  );
}

function Tile({
  label, value, tone, sub,
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'warn';
  // Optional second line (e.g. "Cancels Mar 15") rendered dim
  // below the value. Lets a single tile communicate state-on-top-of-
  // state without needing another grid slot.
  sub?: string;
}) {
  const warn = tone === 'warn';
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid',
        borderColor: warn ? 'rgba(208,64,48,0.35)' : 'var(--line)',
        borderRadius: 14,
        padding: '12px 14px',
      }}
    >
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, color: warn ? '#a8331f' : 'var(--ink)', textTransform: 'capitalize' }}>
        {value}
      </div>
      {sub && (
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.5, marginTop: 4 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function ProfileCard({ profile, email }: { profile: AdminProfileSlice; email: string }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 18 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'baseline' }}>
        <Kv label="VISIBILITY" value={profile.visibility} />
        <Kv label="PHOTOS" value={String(profile.photo_count)} />
        <Kv label="UPDATED" value={formatDate(profile.updated_at)} />
        <Kv label="PUBLISHED" value={profile.published ? formatDate(profile.published_at ?? profile.updated_at) : 'No'} />
        {profile.slug && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1 }}>PUBLIC LINK</div>
            {/* Absolute href: a relative /p/… resolves against admin.wydm.app
                and 404s. webOrigin follows the active environment, so dev
                admin opens the dev site. */}
            <a
              href={`${activeConfig().webOrigin}/p/${profile.slug}`}
              target="_blank"
              rel="noopener"
              style={{ fontSize: 13, color: 'var(--ink)', textDecoration: 'none', borderBottom: '1px dashed var(--line-strong)' }}
            >
              /p/{profile.slug}
            </a>
          </div>
        )}
      </div>

      {/* Bio + identity. Render in a column under the meta row. */}
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {profile.gender && (
          <Kv label="GENDER" value={profile.gender} block />
        )}
        {profile.looking_for.length > 0 && (
          <Kv label="LOOKING FOR" value={profile.looking_for.join(', ')} block />
        )}
        {profile.bio && (
          <div>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>BIO</div>
            <div style={{ fontSize: 14, color: 'var(--ink)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{profile.bio}</div>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--line)', fontSize: 11, color: 'var(--ink-3)' }} className="mono">
        Owner: {email}
      </div>
    </div>
  );
}

function SubCard({ sub }: { sub: AdminSubSlice }) {
  const isLife = sub.is_lifetime;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="mono" style={{
            background: 'var(--chip)',
            color: 'var(--ink)',
            fontSize: 10,
            letterSpacing: 1,
            padding: '3px 8px',
            borderRadius: 100,
            textTransform: 'uppercase',
            fontWeight: 700,
          }}>{sub.vendor}</span>
          <SubStatusChip status={sub.status} />
          {isLife && (
            <span className="mono" style={{ background: 'var(--coral)', color: '#fff', fontSize: 10, letterSpacing: 1, padding: '3px 8px', borderRadius: 100, fontWeight: 700 }}>LIFETIME</span>
          )}
        </div>
        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          {formatDate(sub.created_at)}
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: 'var(--ink-2)', marginTop: 10 }}>
        <Kv label="PRICE" value={sub.price_id} mono />
        <Kv label="VENDOR SUB" value={sub.vendor_subscription_id} mono />
        {!isLife && sub.current_period_end && (
          <Kv label="PERIOD ENDS" value={formatDateTime(sub.current_period_end)} />
        )}
        {sub.cancel_at && (
          <Kv label="CANCELS AT" value={formatDateTime(sub.cancel_at)} />
        )}
      </div>
    </div>
  );
}

function SubStatusChip({ status }: { status: AdminSubSlice['status'] }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    active:    { bg: 'rgba(56,142,60,0.12)', fg: '#2e7d32' },
    past_due:  { bg: 'rgba(232,154,110,0.18)', fg: '#7b3614' },
    cancelled: { bg: 'var(--chip)', fg: 'var(--ink-3)' },
    expired:   { bg: 'rgba(208,64,48,0.10)', fg: '#a8331f' },
  };
  const c = colors[status] ?? { bg: 'var(--chip)', fg: 'var(--ink-3)' };
  return (
    <span className="mono" style={{ background: c.bg, color: c.fg, fontSize: 10, letterSpacing: 1, padding: '3px 8px', borderRadius: 100, fontWeight: 700, textTransform: 'uppercase' }}>
      {status}
    </span>
  );
}

function Kv({ label, value, block, mono }: { label: string; value: string; block?: boolean; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: block ? '100%' : undefined }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1 }}>{label}</div>
      <div className={mono ? 'mono' : undefined} style={{ fontSize: mono ? 12 : 13, color: 'var(--ink)', textTransform: mono ? 'none' : 'capitalize', overflowWrap: 'anywhere' }}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: UserRow['status'] }) {
  const colors: Record<UserRow['status'], { bg: string; fg: string }> = {
    active: { bg: 'rgba(56,142,60,0.12)', fg: '#2e7d32' },
    suspended: { bg: 'rgba(208,64,48,0.12)', fg: '#a8331f' },
    soft_deleted: { bg: 'var(--chip)', fg: 'var(--ink-3)' },
    hard_deleted: { bg: 'var(--chip)', fg: 'var(--ink-3)' },
  };
  const c = colors[status] ?? colors.active;
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 100, background: c.bg, color: c.fg, fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }} className="mono">
      {status}
    </span>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13, marginBottom: 16 }}>
      {children}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '12px 0' }}>{children}</div>;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// ───── payments section ──────────────────────────────────────────────────

// PaymentsSection loads the per-user charges via
// api.admin.users.userPayments and reuses the shared PaymentsTable
// so the row render stays in lockstep with the global
// /dashboard/payments page.
function PaymentsSection({ userID, onError: _onError }: { userID: string; onError: (msg: string) => void }) {
  const token = adminSession.getToken();
  const data = useQuery(
    api.admin.users.userPayments,
    token && userID ? { token, userId: userID as Id<'users'> } : 'skip',
  );
  const payments = (data?.payments ?? null) as AdminPayment[] | null;

  return (
    <section style={{ marginBottom: 28 }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
        Payments {payments ? `· ${payments.length}` : ''}
      </div>
      {!payments ? (
        <Hint>Loading…</Hint>
      ) : (
        <PaymentsTable payments={payments} showUser={false} />
      )}
    </section>
  );
}

// ───── audit trail section ───────────────────────────────────────────────

// AuditTrailSection loads the per-user audit feed in pages of 50 and
// renders each entry as a chip-style row. The "Download PDF" button
// in the section header fetches the chargeback-evidence PDF as a
// blob and triggers a browser download (we can't use a plain
// <a download> because the admin auth lives in a Bearer header).
function AuditTrailSection({ userID, onError }: { userID: string; onError: (msg: string) => void }) {
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const pageSize = 50;

  const token = adminSession.getToken();
  const data = useQuery(
    api.admin.users.userAuditTrail,
    token && userID ? { token, userId: userID as Id<'users'>, page, pageSize } : 'skip',
  );
  const trail = (data ?? null) as UserAuditTrail | null;
  const loading = data === undefined;

  const onDownload = async () => {
    setDownloading(true);
    try {
      const { blob, filename } = await adminAPI.downloadUserAuditTrailPDF(userID);
      // Synthesise a temporary <a> + click() — only way to trigger
      // a "save as" with a server-suggested filename in all browsers.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a short delay so Safari has time to start the
      // download (it reads the blob URL asynchronously).
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      onError(friendlyError(e, 'downloadUserAuditTrailPDF'));
    } finally {
      setDownloading(false);
    }
  };

  const totalPages = trail ? Math.max(1, Math.ceil(trail.total / pageSize)) : 1;

  return (
    <section style={{ marginBottom: 28 }}>
      <div
        style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 12, marginBottom: 8, flexWrap: 'wrap',
        }}
      >
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>
          Audit trail {trail ? `· ${trail.total} events` : ''}
        </div>
        <button
          type="button"
          onClick={() => void onDownload()}
          disabled={downloading}
          style={{
            background: 'var(--ink)',
            color: 'var(--bg)',
            border: 'none',
            borderRadius: 100,
            padding: '7px 14px',
            fontSize: 12,
            fontWeight: 600,
            cursor: downloading ? 'wait' : 'pointer',
            opacity: downloading ? 0.6 : 1,
            fontFamily: 'var(--font-ui)',
          }}
          title="Generate a PDF with identity, subscriptions, and full activity for chargeback evidence."
        >
          {downloading ? 'Generating…' : 'Download PDF →'}
        </button>
      </div>

      {loading && !trail ? (
        <Hint>Loading…</Hint>
      ) : !trail || trail.entries.length === 0 ? (
        <Hint>No recorded activity for this user.</Hint>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {trail.entries.map((e) => <AuditRow key={e.id} entry={e} />)}
          </div>
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, marginTop: 14, flexWrap: 'wrap',
              }}
            >
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1 }}>
                Page {trail.page} / {totalPages}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  style={paginationBtn(page <= 1)}
                >
                  ← Newer
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  style={paginationBtn(page >= totalPages)}
                >
                  Older →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function AuditRow({ entry }: { entry: UserAuditEntry }) {
  // Status chip colour: 2xx green, 3xx grey, 4xx amber, 5xx red.
  const status = entry.status ?? 0;
  const statusColor =
    status === 0 ? 'var(--ink-3)' :
    status < 300 ? '#2e7d32' :
    status < 400 ? 'var(--ink-3)' :
    status < 500 ? '#a86b00' :
    '#a8331f';
  const actorLabel =
    entry.actor_role === 'admin' ? 'Admin' :
    entry.actor_role === 'user'  ? 'User'  :
    'System';
  return (
    // Fixed-basis flex columns: one line when there's room (the action
    // column absorbs the slack, like a 1fr track); on a phone it wraps to
    // time + actor / action / status + IP.
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: '10px 12px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        alignItems: 'center',
        fontSize: 12,
      }}
    >
      <div className="mono" style={{ color: 'var(--ink-3)', flex: '0 0 170px' }}>
        {formatDateTime(entry.created_at)}
      </div>
      <div className="mono" style={{
        fontSize: 10, letterSpacing: 1, color: 'var(--ink-2)',
        background: 'var(--chip)', padding: '2px 6px',
        borderRadius: 100, textAlign: 'center', flex: '0 0 70px',
      }}>
        {actorLabel}
      </div>
      <div style={{ color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 280px', minWidth: 0 }}>
        <span style={{ fontWeight: 600 }}>{entry.action}</span>
        {entry.path && entry.path !== entry.action && (
          <span className="mono" style={{ color: 'var(--ink-3)', marginLeft: 8, fontSize: 11 }}>
            {entry.method} {entry.path}
          </span>
        )}
      </div>
      <div className="mono" style={{ color: statusColor, textAlign: 'right', flex: '0 0 60px', marginLeft: 'auto' }}>
        {entry.status || '—'}
      </div>
      <div className="mono" style={{ color: 'var(--ink-3)', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '0 0 120px' }}>
        {entry.ip || '—'}
      </div>
    </div>
  );
}

function paginationBtn(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? 'var(--chip)' : 'var(--surface)',
    color: disabled ? 'var(--ink-3)' : 'var(--ink)',
    border: '1px solid var(--line-strong)',
    borderRadius: 100,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'var(--font-ui)',
    opacity: disabled ? 0.5 : 1,
  };
}

